'use client';

import { useQuery } from '@tanstack/react-query';

import { unitValidationKeys } from '@/hooks/queries/courseQueryKeys';
import { validateUnitService } from '@/services/httpServices/CourseServices';
import type {
  ValidateUnitResponse,
  ValidateUnitServiceConfig,
} from '@/services/httpServices/CourseServices/types';

import { useQueryConfig } from './useQueryConfig';

export function useOptimizedUnitValidation(
  config?: ValidateUnitServiceConfig,
  enabled?: boolean,
) {
  const { isEnabled } = useQueryConfig();

  return useQuery<ValidateUnitResponse>({
    queryKey: unitValidationKeys(config),
    queryFn: async () => {
      if (config === undefined || config === null) {
        throw new Error('Unit validation config is required');
      }

      if (config.params === undefined || config.params === null
        || config.params.body === undefined || config.params.body === null
        || config.params.body.unitId === undefined || config.params.body.unitId === null
        || config.params.body.unitId === '') {
        throw new Error('Unit validation requires valid unitId');
      }

      return validateUnitService(config);
    },
    enabled: isEnabled && (enabled ?? (
      config !== undefined && config !== null
      && config.params?.body?.unitId !== undefined
      && config.params.body.unitId !== null
      && config.params.body.unitId !== ''
    )),
    refetchOnWindowFocus: true,
    staleTime: 0,
    retry: (failureCount, error) => {
      if (error instanceof Error
        && (error.message.includes('config is required')
          || error.message.includes('requires valid unitId'))) {
        return false;
      }
      return failureCount < 3;
    },
  });
}
